import { JsonSchemaConfiguration } from "../../../sg-schema-form";

import { IConfiguredComponents, defaultConfiguredComponents } from "./Components";
import { useDefaultResolveLocationFilters, TResolveLocationFilters } from "./default/useResolveLocationFilters";
import { EProcessingMarkingPublicity } from "../interfaces/IObservation";
import { IRegisterSettings, IUseGetLocationDataHook } from "../../../sg-kohderekisterit/IKohderekisteri";
import { bestGuessHostedInDevelopment, makeFailure, makeSuccess, TResult, createUnloaded, TLoadable } from "../../../sg-core";

export type TConfigFn = (config: ApplicationConfiguration) => Promise<ApplicationConfiguration>;


/**
 * A wrapper class for the configuration options for the entire WorkPermit-application.
 * The class is designed to be easily configurable for different customer needs.
 */
export class ApplicationConfiguration {

    //
    //   COMPONENTS
    //

    public components: IConfiguredComponents = defaultConfiguredComponents;

    //
    //  SETTINGS
    //
    public activated: TLoadable<boolean> = createUnloaded();
    public observationPublicByDefault: boolean = true;
    public applicationMenuHref: string | null = process.env.REACT_APP_APPLICATION_MENU_HREF || "https://gate.systemsgarden.com/";
    public siteLogoHref: string = process.env.PUBLIC_URL + (process.env.REACT_APP_SITE_LOGO || "/site-logo.png");

    public showDeveloperSettings: boolean = bestGuessHostedInDevelopment;
    public showRawApplicationErrors: boolean = bestGuessHostedInDevelopment;
    public testenviromentBackground: boolean = bestGuessHostedInDevelopment;
    public showDevelopmentLinks: boolean = false;
    public showTenantSwitch: boolean = false;

    public emailSupport: boolean = true;
    public smsSupport: boolean = true;
    public mapSupport: boolean = true;

    public companyName: string = "Systems Garden Oy";
    public companyAddress: string = "Upseerinkatu 3, 02600 Espoo";

    public showLanguageSelect: boolean = false;
    public languages: Array<string> = ["fi"];
    public gateDirectUrl: string = "https://gate.systemsgarden.com/direct2";


    public allowEditingObservation: boolean = false;
    public hideEmptyFieldsFromObservationDetails: boolean = false;
    public archiveLimit: string | undefined = undefined;
    public minimumSearchQueryLength: number = 3;
    public showUniqueObservationId: boolean = true;

    public allowChangingProcessingMarkingPublicity: boolean = false;
    public processingMarkingPublicityDefaultValue: EProcessingMarkingPublicity = EProcessingMarkingPublicity.All


    public registerSettings: IRegisterSettings = {};

    public useGetLocationData: IUseGetLocationDataHook = () => ({
        getLocationData: async () => makeFailure(new Error("Unconfigured hook"))
    });
    //
    // FILTERS
    //

    public useResolveLocationFilters: () => TResolveLocationFilters = useDefaultResolveLocationFilters
    public testSchema = { schema: {}, uiSchema: {} };

    //
    // JSON Schema
    //

    private jsonSchemaConfig?: JsonSchemaConfiguration;

    public addJsonSchemaForm(fn: (options: JsonSchemaConfiguration) => void) {
        const jsonSchemaConfig = new JsonSchemaConfiguration();

        fn(jsonSchemaConfig);
        jsonSchemaConfig.build();

        this.jsonSchemaConfig = jsonSchemaConfig;
    }

    public modifyJsonSchemaForm(fn: (options: JsonSchemaConfiguration) => void) {
        const jsonSchemaConfig = this.jsonSchemaConfig;
        if (jsonSchemaConfig) {
            fn(jsonSchemaConfig);
            this.jsonSchemaConfig = jsonSchemaConfig;
        };
    };

    // Config getters
    public get jsonSchema(): JsonSchemaConfiguration {
        this.ensureExists("jsonSchemaConfig");
        return this.jsonSchemaConfig!;
    }


    //
    //  PRIVATES
    //

    // A simple throwing function for ensuring that a specific config exists.
    private ensureExists(name: string) {
        if (!this[name as keyof ApplicationConfiguration]) {
            throw new Error(`Failed to access '${name}'. The property was undefined`);
        }
    }
}

export const createApplicationConfiguration = async (fn: (config: ApplicationConfiguration) => Promise<ApplicationConfiguration>): Promise<TResult<ApplicationConfiguration>> => {
    let config = new ApplicationConfiguration();

    try {
        config = await fn(config);
    } catch (e) {
        console.error("[Fatal]: ConfigurationError: ", e);
        return makeFailure(e as Error)
    }

    return makeSuccess(config);
}
